/**
 * SummarySection — те сорок секунд, которые решают, будет ли прочитано
 * остальное.
 *
 * Без карточки и без фона: это не блок отчёта, а его первый экран. Три
 * пункта стоят рядом по горизонтали — так видно, что их именно три и что
 * они равнозначны, а не выстроены в очередь, где третий уже за сгибом.
 *
 * Каждый пункт написан так, чтобы по нему можно было действовать: число в
 * абсолюте и в процентах, следствие обычными словами, и кнопка в тот этаж,
 * где это разворачивается. Читатель не должен искать, где живёт
 * подтверждение утверждения.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Button} from 'ui/components/atoms/Button';
import {Row} from 'ui/components/layout/Row';
import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {SECTION_BY_ANCHOR, paletteTone, type PaletteTone} from '../content';
import {floorLabel} from '../floors';
import {MEASURE, TABULAR} from '../helpers';
import type {SectionId, SummaryPoint} from '../types';

export interface SummarySectionProps {
  points: SummaryPoint[];
  /**
   * Какие этажи на странице есть. Якорь вывода приходит из схемы
   * генератора и может указывать на этаж, которого в этом отчёте нет, —
   * кнопка тогда не рисуется вовсе: молча не прокрутившаяся страница
   * читается как поломка.
   */
  present: Record<SectionId, boolean>;
  onNavigate: (id: SectionId) => void;
}

const Column = styled(Stack)({
  flex: '1 1 260px',
  minWidth: 240,
});

/** Тонкая цветная линия сверху — единственный носитель тяжести пункта. */
const Rule = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: PaletteTone}>(({theme, $tone}) => ({
  height: 3,
  width: 48,
  borderRadius: 2,
  backgroundColor: theme.palette[$tone].main,
  marginBottom: spacingPx('s'),
}));

const Title = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
});

/**
 * Сумма в заголовке набирается цветом тяжести пункта — тем же, что и
 * линейка над ним. Пункт про деньги должен быть узнаваем по числу, а не по
 * трёхпиксельной черте: именно число читатель ищет глазами.
 */
const Emphasis = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$tone: PaletteTone}>(({theme, $tone}) => ({
  display: 'inline',
  ...TABULAR,
  color: theme.palette[$tone].main,
}));

const Detail = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
  maxWidth: MEASURE,
});

const Actions = styled(Box)({
  marginTop: 'auto',
  paddingTop: spacingPx('s'),
});

/**
 * Сшивает сегменты заголовка, доставляя пробел там, где его нет.
 *
 * Заголовок приходит кусками: «Цена обращения», выделенное «5 499,44 ₽»,
 * «вместо плановых 3 000 ₽». Разделитель между ними — забота отправителя, и
 * на реальных данных он приходит не всегда: на боевом отчёте три пункта из
 * четырёх склеились в «Цена обращения5 499,44 ₽вместо плановых».
 *
 * Чинить это словами в данных нельзя — сегмент, начинающийся с пробела,
 * выглядит как опечатка и первым же рефакторингом будет обрезан. Поэтому
 * пробел доставляется здесь, и только когда его нет с обеих сторон стыка:
 * там, где отправитель его прислал, мы ничего не трогаем.
 */
function joinSegments(segments: SummaryPoint['title']): SummaryPoint['title'] {
  return segments.map((segment, index) => {
    if (index === 0) return segment;
    const prev = segments[index - 1].text;
    // Пробел ищем любой, включая неразрывный: типограф уже прошёлся по
    // тексту и мог заменить обычный на U+00A0. Записан escape-ом — в
    // исходнике невидимый символ запрещён правилом no-irregular-whitespace.
    const SPACE = /[\s\u00A0]/;
    const glued = !SPACE.test(prev.slice(-1)) && !SPACE.test(segment.text.slice(0, 1));
    return glued ? {...segment, text: ` ${segment.text}`} : segment;
  });
}

export function SummarySection({points, present, onNavigate}: SummarySectionProps) {
  return (
    <Row gap='l' alignY='stretch' wrap='wrap' reflowAt='md'>
      {points.map(point => {
        const target = SECTION_BY_ANCHOR[point.anchor];
        return (
          <Column key={point.anchor + point.detail} gap='s'>
            <Rule $tone={paletteTone(point.severity)} />
            <Title>
              {joinSegments(point.title).map((segment, index) =>
                segment.emphasis ? (
                  // Ключ по индексу: сегменты — куски одной строки, они не
                  // переставляются и не удаляются поодиночке.
                  <Emphasis as='span' key={index} $tone={paletteTone(point.severity)}>
                    {segment.text}
                  </Emphasis>
                ) : (
                  segment.text
                ),
              )}
            </Title>
            <Detail>{point.detail}</Detail>
            {present[target] && (
              <Actions>
                <Button
                  size='m'
                  variant='outline'
                  color='neutral'
                  rightIcon='arrow_downward'
                  onClick={() => onNavigate(target)}
                >
                  Подробнее в «{floorLabel(target)}»
                </Button>
              </Actions>
            )}
          </Column>
        );
      })}
    </Row>
  );
}
